import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";

import AppButton from "../../../../../AppComponents/AppButton";
import AppFormLabel from "../../../../../AppComponents/Forms/AppFormLabel";
import useDocumentTitle from "../../../../../Hooks/useDocumentTitle";
import { HocBackdropLoader } from "../../../../../HOC/HocBackdropLoader";
import { AppForm, AppFormField } from "../../../../../AppComponents/Forms";
import AppReactSelectField from "../../../../../AppComponents/Forms/AppFormReactSelect";
import {
  StyledHeaderLeft,
  StyledTableHeader,
  StyledTableHeaderTitle,
} from "../../../../../Customization/CommonElements";
import { ConversationService } from "../../../../../Services/Apis/ConversationService";
import { useCurrentBot } from "../../../../../Store/Slices/DashboardSlices";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import AppModel from "../../../../../Hooks/useModel/AppModel";
import { useNotification } from "../../../../../Hooks/useNotification/useNotification";
import AppIcon from "../../../../../AppComponents/AppIcon";
import { FormikHelpers } from "formik";
import { useParams } from "react-router-dom";
import { isObject } from "../../../../../Modules/FlowBuilder/Components/Editor/utils/common";
import { isEmptyString } from "../../../../FlowBuilder/ui-components/editor/utils/common";
import { useAppSelector } from "../../../../../Store";
import { useAuthStore } from "../../../../../Store/Slices/AuthSlice";

// Types for intent, journey, and node
interface Journey {
  displayOrder(data: any, displayOrder: any): Node[];
  qid: string;
  name: string;
}

interface Node {
  nodeId: string;
  value: string;
  displayOrder:string;
}

interface Intent {
  qid?: string;
  intent: string;
  journey: Journey | null;
  node: Node | null;
  childNodes: Node[];
}

const maxIntents = 10;

const initialIntent: Intent = {
  intent: "",
  journey: null,
  node: null,
  childNodes: [],
};

const initialValues = {
  intents: [initialIntent],
};

const validationSchema = Yup.object().shape({
  intent: Yup.string()
    .required("Intent is required")
    .min(2, "Intent must be at least 2 characters")
    .max(15, "Intent must be at most 15 characters"),
  journey: Yup.object()
    .nullable()
    .required("Journey is required"),
  node: Yup.object()
    .nullable()
    .required("Node is required"),
});

const IntentFormContent = () => {
  const currentBot = useSelector(useCurrentBot);
  const [flowOptions, setFlowOptions] = useState<Journey[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIntent, setModalIntent] = useState<Intent>(initialIntent);
  const [intents, setIntents] = useState<any[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [intentToDelete, setIntentToDelete] = useState<Intent | null>(null);
  const { bot_id }: any = useParams(); // temp-changes
  const { userConfig } = useAppSelector(useAuthStore);
  const HEADERS = {
    appid: JSON.stringify({
      botid: currentBot.bot_id || bot_id,
    }),
  };
  const { showAlert } = useNotification();
  const openConfirmDeleteModal = (intent: Intent) => {
    setIntentToDelete(intent);
    setIsConfirmModalOpen(true);
  };
  const confirmDelete = () => {
    if (intentToDelete?.qid) {
      ConversationService.deleteIntent(intentToDelete.qid, HEADERS)
        .then(() => {
          getIntentList();
          setIsConfirmModalOpen(false);
          showAlert("Intent deleted successfully", "success");
        })
        .catch((error) => {
          console.error("Delete failed", error);
          setIsConfirmModalOpen(false);
        });
    }
  };
const getFlowNodeTextValue = (data: any[],stage:any): Node[] => {
  const stageMap: Record<string | number, number> = {}; // Tracks item count per stage
  return data?.map((item) => {
    // Increment stage count
    if (!stageMap[stage]) {
      stageMap[stage] = 1;
    } else {
      stageMap[stage]++;
    }

    let rawValue =
      item?.response?.[0]?.value?.text ||
      item?.response?.[0]?.interactive?.body ||
      item?.response?.[0]?.value ||
      "un-title";
    if(isObject(item?.response?.[0]?.value)){
      if(item?.response?.[0]?.value?.address){
        rawValue = !isEmptyString(item?.response?.[0]?.value?.address) ? item?.response?.[0]?.value?.address : "<p>Untitled</p>"; 
      }else{
       rawValue = !isEmptyString(item?.response?.[0]?.value?.text) ? item?.response?.[0]?.value?.text : "<p>Untitled</p>"; 
      }
    }else if (Array.isArray(rawValue)) {
      rawValue = rawValue?.[0]?.text || "un-title";
    }
    // Strip HTML
    const strippedValue = rawValue.replace(/<[^>]*>/g, "").trim();
    const text = strippedValue.substring(0, 20) + (strippedValue.length > 20 ? "..." : "");

    return {
      nodeId: item.nodeId || item.node_id || "",
      value: `${stage}.${stageMap[stage]} ${text}`,
      displayOrder: item?.displayOrder,
    };
  });
};


  const openAddModal = async () => {
    if (intents?.length >= maxIntents) return;
     setModalIntent({ ...initialIntent })
     setIsModalOpen(true);
  };

  const openEditModal = async (intent: Intent) => {
    // Load child nodes for the journey if it exists
    if (intent.journey?.qid) {
      let childNodes: Node[] = [];
      try {
        const res = await ConversationService.getChildFlowList(
          intent.journey.qid,
          HEADERS
        );
        if (Array.isArray(res?.data?.data)) {
          childNodes = getFlowNodeTextValue(res.data.data, intent.journey?.displayOrder || 1);
        }
      } catch (err) {
        console.error(err);
      }
      
      setModalIntent({
        ...intent,
        childNodes,
      });
    } else {
      setModalIntent(intent);
    }
    
    setIsModalOpen(true);
  };

  const handleAddOrEditIntent = (values: Intent): Intent => {
    const mergedJourney = {
      ...(modalIntent?.journey || {}),
      nodeId: modalIntent?.node?.nodeId || "",
      displayOrder:modalIntent?.node?.displayOrder 
    };

    let data: any = {
      intent: modalIntent?.intent,
      journey: mergedJourney,
      node: modalIntent?.node,
    };
    if (modalIntent?.qid) {
      data.qid = modalIntent?.qid;
    }

    ConversationService.createIntent(data, HEADERS)
      .then((res: any) => {
        getIntentList();
        setIsModalOpen(false);
        showAlert(`Intent ${modalIntent?.qid ? "Updated" :"Created"} Successfully`, "success");
      })
      .catch((error: any) => {
        console.log("==> error", error?.response?.data?.error);
        showAlert(
          `${error?.response?.data?.error || "Failed to add intent"}`,
          "error"
        );
      });

    // Return the updated or added intent only
    return modalIntent;
  };

  const handleJourneyChange = async (val: Journey | null) => {
    if (!val) {
      setModalIntent({
        ...modalIntent,
        journey: null,
        node: null,
        childNodes: [],
      });
      return;
    }

    let childNodes: Node[] = [];
    try {
      const res = await ConversationService.getChildFlowList(val.qid, HEADERS);
      if (Array.isArray(res?.data?.data)) {
        childNodes = getFlowNodeTextValue(res.data.data,val.displayOrder||1);
      }
    } catch (err) {
      console.error(err);
    }
    setModalIntent({ ...modalIntent, journey: val, node: null, childNodes });
  };

  useEffect(() => {
    ConversationService.getFlowList(HEADERS)
      .then((res) => {
        if (Array.isArray(res?.data?.data)) setFlowOptions(res?.data?.data);
      })
      .catch(console.error);
  }, []);

  const getIntentList = () => {
    const headers = {
      appid: JSON.stringify({
        botid: currentBot.bot_id || bot_id,
      }),
    };
    ConversationService.getIntentList(headers)
      .then((response: any) => {
        setIntents(response?.data?.data);
      })
      .catch((error: any) => {
        setIntents([]);
      });
  };

  useEffect(() => {
    getIntentList();
  }, []);

  return (
    <>
      <StyledTableHeader style={{ marginBottom: '4px' }} >
        <StyledHeaderLeft>
          <StyledTableHeaderTitle isActive={false}>
            {/* <ArrowBackIosRoundedIcon />  */}
            Intent Management
          </StyledTableHeaderTitle>
        </StyledHeaderLeft>
      </StyledTableHeader>
    <IntentWrap>
      <StyledBoxWrap style={{marginRight:"6rem" }} >
        <FlexRow>
          <AppFormLabel>Intents (Max {maxIntents})</AppFormLabel>
          <AppButton
            isDisabled={intents?.length >= maxIntents}
            onClick={openAddModal}
          >
            Add Intent
          </AppButton>
        </FlexRow>

        <TableContainer component={Paper}>
     <Table stickyHeader>
         <TableHead>
              <TableRow sx={{ "& .MuiTableCell-root": { padding: "14px" } }}>
                <TableCell style={{ width: "20%" }}>Intent</TableCell>
                <TableCell style={{ width: "20%" }}>Flow</TableCell>
                <TableCell style={{ width: "20%" }}>Node</TableCell>
                <TableCell style={{ width: "20%" }}>Last Updated</TableCell>
                <TableCell style={{ width: "20%" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            </Table>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
             <Table>
            <TableBody>
              {intents?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No intent added
                  </TableCell>
                </TableRow>
              ) : (
                intents?.map((intent) => (
                  <TableRow key={intent.id} sx={{ "& .MuiTableCell-root": { padding: "14px" } }}>
                    <TableCell style={{ width: "20%" }}>{intent?.intent}</TableCell>
                    <TableCell style={{ width: "20%" }}>{intent?.journey?.name || "-"}</TableCell>
                    <TableCell style={{ width: "20%" }}>{intent?.node?.value || "-"}</TableCell>
                    <TableCell style={{ width: "20%" }}>{intent?.updatedAt || "-"}</TableCell>
                    <TableCell>
                      <Stack
                        style={{
                          display: "flex",
                          gap: "1.5rem",
                          alignItems: "center",
                          flexDirection: "row",
                        }}
                      >
                        <AppIcon
                          title={"ri-pencil-line"}
                          onClick={() => openEditModal(intent)}
                          size="22px"
                          color="#7E8392"
                          hoverColor={"#282828"}
                          style={{
                            marginRight: "1rem !important",
                          }}
                        />
                        <AppIcon
                          title={"ri-delete-bin-6-line"}
                          onClick={() => openConfirmDeleteModal(intent)}
                          size="20px"
                          color="#7E8392"
                          hoverColor={"#F34E4E"}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </TableContainer>
      </StyledBoxWrap>

      <AppModel
        isShowing={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        divStyle={{ padding: 0 }}
        PaperProps={{
          style: { boxShadow: "none", width: "500px", height: "auto" },
        }}
      >
        <ModalContainer>
          <div className="header">
            <h4>{modalIntent?.qid ? "Edit Intent" : "Add Intent"}</h4>
            <div className="header-right">
              <span onClick={() => setIsModalOpen(false)}>
                <CloseIcon />
              </span>
            </div>
          </div>
          <AppForm
              initialValues={modalIntent}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={(values: Intent, { resetForm }: FormikHelpers<Intent>) => {
                setModalIntent(values);
                handleAddOrEditIntent(values);
                resetForm();
                setIsModalOpen(false);
              }}
            >
          <div className="body">
            <AppFormField
              label="Intent"
              name="intent"
              placeholder="Enter intent"
              value={modalIntent.intent}
              requiredSign={true}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setModalIntent({ ...modalIntent, intent: e.target.value })
              }
            />
            <AppReactSelectField
              label="Journey"
              name="journey"
              options={flowOptions}
              value={modalIntent.journey}
              displayKey="name"
              valueKey="qid"
              onChangeCallbackFn={handleJourneyChange}
              placeholder="Select Flow"
              isClearable
              requiredSign={true}
            />
            <AppReactSelectField
              label="Node"
              name="node"
              options={modalIntent.childNodes || []}
              value={modalIntent?.node}
              displayKey="value"
              valueKey="nodeId"
              onChangeCallbackFn={(val: Node | null) =>
                setModalIntent({ ...modalIntent, node: val })
              }
              placeholder="Select Node"
              isClearable
              isDisabled={!modalIntent.journey}
              requiredSign={true}
            />
            <AppButton type="submit">Save</AppButton>
          </div>
          </AppForm>
        </ModalContainer>
      </AppModel>
      <AppModel
        isShowing={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        divStyle={{ padding: 0 }}
        PaperProps={{
          style: { boxShadow: "none", width: "400px", height: "auto" },
        }}
      >
        <ModalContainer>
          <div className="header">
            <h4>Confirm Delete</h4>
            <div className="header-right">
              <span onClick={() => setIsConfirmModalOpen(false)}>
                <CloseIcon />
              </span>
            </div>
          </div>
          <div className="body">
            <p>
              Are you sure you want to delete the intent{" "}
              <strong>{intentToDelete?.intent}</strong>?
            </p>
            <FlexRow style={{ justifyContent: "flex-end", marginTop: "1rem" }}>
              <AppButton
                variant="grey"
                style={{ width: "20%", margin: "0px 10px" }}
                onClick={() => setIsConfirmModalOpen(false)}
              >
                Cancel
              </AppButton>
              <AppButton
                variant="danger"
                style={{ width: "20%" }}
                onClick={confirmDelete}
              >
                Delete
              </AppButton>
            </FlexRow>
          </div>
        </ModalContainer>
      </AppModel>
    </IntentWrap>
    </>
  );
};

const IntentManagement = () => {
  useDocumentTitle("Intent Management");

  const handleSubmit = (values: { intents: Intent[] }) => {
    console.log("Saved intents:", values.intents);
  };

  return (
    <AppForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <IntentFormContent />
    </AppForm>
  );
};

export default HocBackdropLoader(IntentManagement);

// Styled Components

const IntentWrap = styled.div`
  background-color: #f5f6f8;
  padding: 0 1rem 1rem 1rem;
  height: 100%;
  width: 80vw;
`;

const StyledBoxWrap = styled.div`
  // margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalContainer = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #eee;
    h4 {
      margin: 0;
    }
  }
  .body {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;
